// covered-by: frontends/carrie/src/routes/login/__tests__/LoginView.test.tsx
import { ButtonNew, Icon } from "@mg/dali/src";
import {
  type SsoAuthorizeResponse,
  type WorkosLoginResponse,
} from "@mg/schemas/src/prince/auth";
import { Text } from "@radix-ui/themes";
import { useMutation } from "@tanstack/react-query";
import { Navigate } from "@tanstack/react-router";
import { useState, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { LoginForm } from "./LoginForm";
import { MfaForm } from "./MfaForm";
import { loginRoute } from "./route";

import { LoginLayout } from "../../components/layouts/login";
import { isAskSurface } from "../../config/askSurface";
import {
  applyAskV2InPlaceReload,
  waitForAskV2Flag,
} from "../../config/askV2InPlace";
import {
  ssoAuthorize,
  ssoExchange,
  workOsLogin,
  verifyMfa,
} from "../../services/auth";
import { completeOnboarding } from "../../services/userProfile";
import { errorAnalyticsPayload, useAnalytics } from "../../utils/analytics";
import { isMeaningfulGigsRole } from "../../utils/auth";
import {
  defaultPostAuthLandingKind,
  ensureDefaultPostAuthLandingPath,
} from "../../utils/defaultPostAuthLanding";
import { useAppDispatch, useAppSelector } from "../../utils/hooks";
import { isInvalidMfaCodeError } from "../../utils/mfaCodeError";
import {
  clearMfaRequiredNotice,
  MFA_REQUIRED_ERROR,
  useMfaRequiredNotice,
} from "../../utils/mfaRequired";
import { queryClient } from "../../utils/queryClient";
import { login } from "../../utils/slices/auth";
import { safeStorageSet } from "../../utils/storage";
import { roiRoute } from "../roi/route";
import { ticketsRoute } from "../tickets/route";

function useWorkosLogin() {
  return useMutation({
    mutationKey: ["login"],
    mutationFn: workOsLogin,
  });
}

function useMfaVerification() {
  return useMutation({
    mutationKey: ["verifyMfa"],
    mutationFn: verifyMfa,
  });
}

export function LoginView() {
  const { t } = useTranslation("auth");
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.value);
  const searchParams = loginRoute.useSearch();
  const mfaNotice = useMfaRequiredNotice();
  const [authMode, setAuthMode] = useState<SsoAuthorizeResponse | null>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [mfaData, setMfaData] = useState<WorkosLoginResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [resolvedLandingPath, setResolvedLandingPath] = useState<string | null>(
    null,
  );
  const defaultLandingTelemetrySentRef = useRef(false);
  const ssoExchangeStartedRef = useRef(false);

  const loginMutation = useWorkosLogin();
  const mfaMutation = useMfaVerification();
  const authorizeMutation = useMutation({
    mutationKey: ["sso-authorize"],
    mutationFn: ssoAuthorize,
  });
  const onboardingMutation = useMutation({
    mutationKey: ["complete-onboarding-client"],
    mutationFn: completeOnboarding,
  });

  const posthog = useAnalytics("LoginView");

  const formRef = useRef<HTMLFormElement>(null);

  const validateForm = useCallback(() => {
    if (formRef.current) {
      const formData = new FormData(formRef.current);
      const email = formData.get("email") as string;
      const password = formData.get("password") as string;
      setIsFormValid(!!email && !!password);
    }
  }, []);

  const handleLoginSuccess = (data: WorkosLoginResponse, startTime: number) => {
    // why: this runs from the login mutation's onSuccess, not a component event
    // handler, so an unguarded write escaping here is not caught by any boundary —
    // sendTokenToServiceWorker() and dispatch(login(...)) below would never run and
    // the login flow would die silently on a storage-blocked browser.
    safeStorageSet(window.localStorage, "onboarding_flow", "true");
    if (data.token) {
      onboardingMutation
        .mutateAsync()
        .then((dt) => {
          safeStorageSet(window.localStorage, "token", dt.token);
          // Global from index.html / globals.d.ts
          sendTokenToServiceWorker();
          dispatch(login({ token: dt.token }));
        })
        .catch(() => {
          dispatch(login({ token: data.token as string }));
        });

      posthog.capture("login_success", {
        requestDurationSeconds: (performance.now() - startTime) / 1000,
        referredBy: user?.referredBy,
      });
    } else {
      posthog.capture("login_mfa_step", {
        requestDurationSeconds: (performance.now() - startTime) / 1000,
        referredBy: user?.referredBy,
      });
      setMfaData(data);
    }
  };

  // After WorkOS SSO callback redirect (#sso=1), exchange the short-lived cookie.
  useEffect(() => {
    if (user) return;
    if (typeof window === "undefined") return;
    if (!window.location.hash.includes("sso=1")) return;
    if (ssoExchangeStartedRef.current) return;
    ssoExchangeStartedRef.current = true;
    const startTime = performance.now();
    ssoExchange()
      .then((data) => {
        if (data?.token) {
          handleLoginSuccess(data, startTime);
          window.history.replaceState(
            null,
            "",
            window.location.pathname + window.location.search,
          );
        }
      })
      .catch((error: Error) => {
        setErrorMessage(error.message || "SSO login failed");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot on mount
  }, [user]);

  const startSsoRedirect = (email: string, enterpriseId?: string) => {
    authorizeMutation.mutate(
      { email, enterpriseId },
      {
        onSuccess: (data) => {
          if (data?.redirectUrl) {
            window.location.assign(data.redirectUrl);
            return;
          }
          if (data?.orgPicker?.length) {
            setAuthMode(data);
            setErrorMessage("Select an organization to continue with SSO.");
            return;
          }
          // password / mg / misconfigured SSO — never leave the button spinning
          // with a silent no-op (classic form always shows Continue with SSO).
          setErrorMessage(
            "SSO is not available for this email. Log in with your password, or contact your admin.",
          );
        },
        onError: (error) => setErrorMessage(error.message),
      },
    );
  };

  const handleLoginSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;
    const startTime = performance.now();
    setErrorMessage("");

    loginMutation.mutate(
      { email, password },
      {
        onSuccess: (data) => {
          // why: a 2xx with an empty body resolves data as undefined; the old
          // `data!` threw inside onSuccess and killed the login flow silently.
          if (data == null) {
            setErrorMessage(
              "Login did not return a session. Please try again.",
            );
            return;
          }
          handleLoginSuccess(data, startTime);
        },
        onError: (error) => {
          setErrorMessage(error.message);
          posthog.capture("login_failure", {
            requestDurationSeconds: (performance.now() - startTime) / 1000,
            referredBy: user?.referredBy,
            ...errorAnalyticsPayload(error),
          });
        },
      },
    );
  };

  const handleMfaVerification = (otp: string) => {
    const startTime = performance.now();
    if (mfaData) {
      mfaMutation.mutateAsync(
        {
          authenticationChallengeId: mfaData.authenticationChallenge
            ?.id as string,
          pendingAuthenticationToken:
            mfaData.pendingAuthenticationToken as string,
          code: otp,
        },
        {
          onSuccess: (data) => {
            // why: same empty-2xx case as password login — `data!.token` threw
            // after the OTP was already accepted, stranding the user on MFA.
            if (data?.token == null) {
              setErrorMessage(
                "Login did not return a session token. Please try again.",
              );
              return;
            }
            dispatch(login({ token: data.token as string }));
            posthog.capture("mfa_success", {
              requestDurationSeconds: (performance.now() - startTime) / 1000,
              referredBy: user?.referredBy,
            });
          },
          onError: (error) => {
            // A refused code is the commonest failure here, and it is the one
            // case where the server's sentence says nothing about the code
            // (`invalid_mfa_code`). Everything else — a vendor outage, a
            // network blip — keeps the message it came with.
            setErrorMessage(
              isInvalidMfaCodeError(error)
                ? t("mfaForm.invalidCode")
                : error.message,
            );
            posthog.capture("mfa_failure", {
              requestDurationSeconds: (performance.now() - startTime) / 1000,
              referredBy: user?.referredBy,
            });
          },
        },
      );
    }
  };

  // The refusal arrives two ways: in the URL, when the http layer redirected
  // here from another screen, or in the notice slot, when it arrived while the
  // user was already on this screen and the http layer deliberately did not
  // reload (it would re-fire the request that refused). Same message either way.
  const showMfaRequiredNotice =
    searchParams.error === MFA_REQUIRED_ERROR || mfaNotice != null;
  const noticeMessage = showMfaRequiredNotice
    ? t("login.mfaRequiredError")
    : searchParams.error === "session-expired"
      ? t("login.sessionExpiredError")
      : null;
  const defaultEmail = searchParams.email || mfaNotice?.email || undefined;

  const isAlreadyAuthed = user != null;

  // The refusal is behind us once a session exists — never carry its notice
  // into a later visit. Keyed on the session rather than on each success
  // callback, because there are three of them (password login, `MfaForm`
  // verify, the SSO cookie exchange) and any one of them alone would leave the
  // other two paths showing a stale notice after the user signs out again.
  useEffect(() => {
    if (user) clearMfaRequiredNotice();
  }, [user]);

  const hasExplicitRedirect = searchParams.redirect != null;
  // why: MG staff on a non-Ask host still default to `/roi`. On Ask surfaces
  // (`isAskSurface()`) post-auth landing uses ensureDefaultPostAuthLandingPath
  // (V2 home) even though V2 now registers `/roi` for deep links.
  const landsOnRoi = isMeaningfulGigsRole() && !isAskSurface();
  const needsAsyncDefault =
    isAlreadyAuthed && !hasExplicitRedirect && !landsOnRoi;

  useEffect(() => {
    if (!needsAsyncDefault) return;
    if (resolvedLandingPath != null) return;
    let cancelled = false;
    void (async () => {
      const landingPromise = ensureDefaultPostAuthLandingPath(queryClient);
      const flagOn = await waitForAskV2Flag();
      if (cancelled) return;
      if (applyAskV2InPlaceReload(flagOn)) return;
      try {
        const target = await landingPromise;
        if (cancelled) return;
        setResolvedLandingPath(target);
      } catch {
        if (cancelled) return;
        setResolvedLandingPath(ticketsRoute.to);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [needsAsyncDefault, resolvedLandingPath]);

  useEffect(() => {
    if (!isAlreadyAuthed) return;
    if (hasExplicitRedirect) return;
    if (defaultLandingTelemetrySentRef.current) return;
    if (!landsOnRoi && resolvedLandingPath == null) return;
    posthog.capture("post_auth_default_landing", {
      site: "login_already_authed",
      target: landsOnRoi ? "roi" : defaultPostAuthLandingKind(),
    });
    defaultLandingTelemetrySentRef.current = true;
  }, [isAlreadyAuthed, hasExplicitRedirect, landsOnRoi, resolvedLandingPath]);

  if (user) {
    let searchRedirect: string | null;

    if (searchParams.redirect != null) {
      const { redirect } = searchParams;
      if (redirect.startsWith("/")) {
        searchRedirect = redirect;
      } else {
        searchRedirect = `/${redirect}`;
      }
    } else if (landsOnRoi) {
      searchRedirect = roiRoute.to;
    } else {
      searchRedirect = resolvedLandingPath;
    }

    if (searchRedirect == null) {
      return (
        <LoginLayout>
          <div
            data-testid="login-resolving-landing"
            aria-busy="true"
            aria-label={t("login.resolvingLandingAriaLabel")}
          />
        </LoginLayout>
      );
    }

    return <Navigate to={searchRedirect} />;
  }

  return (
    <LoginLayout>
      {!mfaData ? (
        <>
          {searchParams.error || showMfaRequiredNotice ? (
            <Text
              color="red"
              size="2"
              className="mb-4 block"
              data-testid="login-notice"
            >
              {noticeMessage}
            </Text>
          ) : null}
          {authMode?.orgPicker?.length ? (
            <div
              className="mb-4 grid gap-2"
              data-testid="login-org-picker"
              role="list"
              aria-label="Choose organization"
            >
              {authMode.orgPicker.map((org) => (
                <ButtonNew
                  key={org.enterpriseId}
                  variant="outlined"
                  data-testid={`login-org-option-${org.enterpriseId}`}
                  aria-label={`Continue with ${org.name}`}
                  onClick={() => {
                    const email = formRef.current
                      ? (new FormData(formRef.current).get("email") as string)
                      : "";
                    startSsoRedirect(email, org.enterpriseId);
                  }}
                >
                  {org.name}
                </ButtonNew>
              ))}
            </div>
          ) : null}
          <LoginForm
            data-testid="login-form"
            formRef={formRef}
            isValid={isFormValid}
            onSubmit={handleLoginSubmit}
            loading={loginMutation.isPending}
            disabled={loginMutation.isPending}
            error={loginMutation.isError || Boolean(errorMessage)}
            errorMessage={errorMessage}
            defaultEmail={defaultEmail}
            checkValidity={validateForm}
            ssoLoading={authorizeMutation.isPending}
            onSsoClick={() => {
              const email = formRef.current
                ? (new FormData(formRef.current).get("email") as string)
                : "";
              if (!email) {
                setErrorMessage("Enter your work email to continue with SSO.");
                return;
              }
              setErrorMessage("");
              startSsoRedirect(email);
            }}
          />
        </>
      ) : (
        <div>
          <ButtonNew
            variant="ghost"
            data-testid="login-mfa-back-button"
            aria-label={t("login.mfaBackButtonAriaLabel")}
            onClick={() => {
              setMfaData(null);
            }}
          >
            <Icon.ArrowLeft />
            {t("login.backButtonLabel")}
          </ButtonNew>
          <MfaForm
            mfaOptions={mfaData}
            loading={mfaMutation.isPending}
            error={mfaMutation.isError}
            onVerify={handleMfaVerification}
            errorMessage={errorMessage}
          />
        </div>
      )}
    </LoginLayout>
  );
}
